import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  GraduationCap,
  Calendar,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Star,
  Clock,
  ArrowRight,
  Play,
  Pause,
} from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SmartLink } from '@/components/SmartLink';
import { useClubInfo } from '@/context/ClubInfoContext';
import { AnimatedCounter } from '@/components/AnimatedCounter';
import { getNewsIcon, getNewsIconColor, getActivityIcon } from '@/utils/iconMap';
import { filterUpcomingSorted } from '@/utils/dateHelpers';
import anlageImage from '@/assets/anlage.webp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Hero-Hintergrund: Bild vom eigenen Fileserver (relativer Pfad, damit die
// Domain konfigurierbar bleibt und sich aendern kann - siehe BACKEND_URL).
const HERO_IMAGE_PATH = '/api/files/rfv-holle-wuesting/media/0b70a274-5be4-4e20-8b5c-4e1922ecfcf0.jpg';
const HERO_IMAGE_URL = `${BACKEND_URL}${HERO_IMAGE_PATH}`;

// Respektiert die Browser-/OS-Einstellung "Bewegung reduzieren".
const prefersReducedMotion =
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Auf Mobile gibt es keinen Hover, daher startet der Banner pausiert -
// der Nutzer kann ihn manuell wischen oder den Play-Button druecken.
const isSmallScreen =
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 640px)').matches;

export const Home = () => {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [bannerPaused, setBannerPaused] = useState(prefersReducedMotion || isSmallScreen);
  const [news, setNews] = useState([]);
  const [activities, setActivities] = useState([]);
  const [workDuties, setWorkDuties] = useState([]);
  const [bannerSlides, setBannerSlides] = useState([]);
  const { clubInfo } = useClubInfo();
  // „e.V." soll im Hero-Titel nicht alleine in eine eigene Zeile umbrechen.
  // Daher das Leerzeichen vor „e.V." durch ein non-breaking space ersetzen.
  // Nur Anzeige – clubInfo.name bleibt in der DB unverändert.
  const heroTitle = (clubInfo.name || '').replace(/ e\.V\.$/i, ' e.V.');

  // Fetch news, activities, work duties, banner slides from backend
  useEffect(() => {
    const load = async () => {
      try {
        const [newsRes, actRes, wdRes, bnRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/news`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/activities`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/workduties`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/banner-slides`).catch(() => ({ data: [] })),
        ]);
        setNews(Array.isArray(newsRes.data) ? newsRes.data : []);
        setActivities(Array.isArray(actRes.data) ? actRes.data : []);
        setWorkDuties(Array.isArray(wdRes.data) ? wdRes.data : []);
        setBannerSlides(Array.isArray(bnRes.data) ? bnRes.data : []);
      } catch (e) {
        // Silent fail – sections will render empty states
      }
    };
    load();
  }, []);

  // Aktuelles Jahr - Jubiläumsjahre (z.B. 100 Jahre Tradition)

  // Visible banner slides from backend (fallback to a single welcome banner)
  const banners = bannerSlides.filter((s) => s.active !== false);
  const safeBanners =
    banners.length > 0
      ? banners
      : [
          {
            id: 'fallback',
            gradient: 'from-primary to-primary-dark',
            title: `Willkommen beim ${heroTitle}`,
            subtitle: `Tradition, Leidenschaft und Gemeinschaft seit ${clubInfo.foundingYear}`,
            buttonText: '',
            buttonLink: '',
          },
        ];

  // Auto-rotate banners - aber nur, wenn der Nutzer es zulaesst (WCAG 2.2.2).
  useEffect(() => {
    if (safeBanners.length <= 1 || bannerPaused) return undefined;
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % safeBanners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [safeBanners.length, bannerPaused]);

  const stats = [
    { value: `${clubInfo.members}+`, label: 'Mitglieder', icon: Users },
    { value: `${clubInfo.ridingLessonsPerWeek}+`, label: 'Reitstunden/Woche', icon: GraduationCap },
    { value: `${clubInfo.yearsOfTradition}+`, label: 'Jahre Tradition', icon: Calendar },
    { value: `${clubInfo.areaHectares}+`, label: 'Hektar Gelände', icon: MapPin },
  ];

  // Chronological, hide past entries, show the closest 4 on the home page.
  const upcomingEvents = filterUpcomingSorted(activities).slice(0, 4);
  const upcomingWorkDuties = filterUpcomingSorted(workDuties).slice(0, 4);
  const latestNews = news.slice(0, 4);

  const infoBoxes = [
    {
      title: 'Reitunterricht',
      description: 'Qualifizierter Unterricht für Anfänger und Fortgeschrittene',
      image: 'https://images.unsplash.com/photo-1595675759825-6b73f7b37cda?w=600',
      link: '/reitunterricht',
    },
    {
      title: 'Unsere Anlagen',
      description: 'Reithalle, Außenplätze und komfortable Stallungen',
      image: anlageImage,
      link: '/einrichtungen',
    },
    {
      title: 'Turniere',
      description: 'Spannende Wettbewerbe für alle Leistungsklassen',
      image: 'https://images.unsplash.com/photo-1613085411234-9c83af5562d8?w=600',
      link: '/turniere',
    },
  ];

  return (
    <div className="min-h-[100dvh]">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src={HERO_IMAGE_URL}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          onError={(event) => {
            // Ein frisches Deployment hat die historische Media-UUID nicht
            // zwingend im Upload-Verzeichnis. Dann auf das gebuendelte
            // Vereinsanlagen-Bild zurueckfallen statt einen leeren Hero zu zeigen.
            event.currentTarget.onerror = null;
            event.currentTarget.src = anlageImage;
          }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 bg-foreground/40" />

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            Willkommen beim {heroTitle}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg sm:text-xl text-white/90 mb-8"
          >
            Tradition, Leidenschaft und Gemeinschaft seit {clubInfo.foundingYear}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Link to="/mitgliedschaft">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary-dark text-primary-foreground px-8 py-6 text-lg rounded-xl"
              >
                Mitglied werden
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Banner Slider - feste Hoehe, damit der Inhalt darunter beim
          Slide-Wechsel nicht hoch/runter springt. */}
      <section
        className="relative bg-muted overflow-hidden min-h-[220px] flex items-stretch"
        aria-roledescription="carousel"
        aria-label="Aktuelle Hinweise"
        onMouseEnter={() => safeBanners.length > 1 && setBannerPaused(true)}
        onMouseLeave={() => !prefersReducedMotion && setBannerPaused(false)}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentBanner}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
            role="group"
            aria-roledescription="slide"
            aria-label={`Hinweis ${(currentBanner % safeBanners.length) + 1} von ${safeBanners.length}`}
            className={`bg-gradient-to-r ${safeBanners[currentBanner % safeBanners.length].gradient} py-6 px-4 w-full flex items-center justify-center`}
          >
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="font-display text-xl sm:text-2xl font-bold mb-2">
                {safeBanners[currentBanner % safeBanners.length].title}
              </h2>
              <p className="text-sm sm:text-base text-white/90 mb-4">
                {safeBanners[currentBanner % safeBanners.length].subtitle}
              </p>
              {safeBanners[currentBanner % safeBanners.length].buttonText &&
                (() => {
                  const slide = safeBanners[currentBanner % safeBanners.length];
                  const target = slide.linkedArticleId ? `/news/${slide.linkedArticleId}` : slide.buttonLink || '/';
                  return (
                    <Button
                      asChild
                      variant="secondary"
                      className="h-auto min-h-9 max-w-full whitespace-normal bg-white py-2 text-foreground hover:bg-white/90"
                    >
                      <SmartLink href={target}>
                        <span className="min-w-0 text-center [overflow-wrap:anywhere]">{slide.buttonText}</span>
                      </SmartLink>
                    </Button>
                  );
                })()}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Arrows + Dots + Pause-Button */}
        {safeBanners.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => setCurrentBanner((prev) => (prev - 1 + safeBanners.length) % safeBanners.length)}
              aria-label="Vorheriger Hinweis"
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 rounded-full hover:bg-white/30 focus-visible:bg-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white transition-colors text-white"
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => setCurrentBanner((prev) => (prev + 1) % safeBanners.length)}
              aria-label="Nächster Hinweis"
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/20 rounded-full hover:bg-white/30 focus-visible:bg-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white transition-colors text-white"
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>

            {/* Pause-/Play-Button - WCAG 2.2.2 */}
            <button
              type="button"
              onClick={() => setBannerPaused((p) => !p)}
              aria-label={bannerPaused ? 'Karussell fortsetzen' : 'Karussell pausieren'}
              aria-pressed={bannerPaused}
              className="absolute top-3 right-3 p-2 bg-white/20 rounded-full hover:bg-white/30 focus-visible:bg-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white transition-colors text-white"
            >
              {bannerPaused ? (
                <Play className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Pause className="h-4 w-4" aria-hidden="true" />
              )}
            </button>

            {/* Dots - visueller Punkt klein, Hit-Area 44x44 (WCAG 2.5.5) */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex">
              {safeBanners.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentBanner(idx)}
                  aria-label={`Hinweis ${idx + 1} von ${safeBanners.length} anzeigen`}
                  aria-current={idx === currentBanner % safeBanners.length ? 'true' : undefined}
                  className="h-11 w-11 grid place-items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-full"
                >
                  <span
                    className={`block h-3 rounded-full transition-all ${
                      idx === currentBanner % safeBanners.length ? 'w-6 bg-white' : 'w-3 bg-white/50'
                    }`}
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Stats Section */}
      <section className="py-12 lg:py-16 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="text-center hover:shadow-lg transition-shadow border-0 bg-muted/50">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                      <stat.icon className="h-6 w-6 text-primary" />
                    </div>
                    <p className="text-3xl lg:text-4xl font-bold text-primary mb-1" data-testid={`stat-value-${idx}`}>
                      <AnimatedCounter value={stat.value} />
                    </p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Aktuelle Termine */}
      <section className="py-12 lg:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl lg:text-3xl font-bold text-foreground">Aktuelle Termine</h2>
            <Link
              to="/aktivitaeten"
              className="text-primary hover:text-primary-dark text-sm font-medium flex items-center gap-1"
            >
              Alle anzeigen <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {upcomingEvents.map((event) => {
              const EventIcon = getActivityIcon(event);
              return (
                <Card
                  key={event.id}
                  as="article"
                  aria-labelledby={`home-event-${event.id}-title`}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardContent className="p-4 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <EventIcon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3
                          id={`home-event-${event.id}-title`}
                          className="font-display font-semibold text-foreground truncate min-w-0"
                        >
                          {event.name}
                        </h3>
                        {event.highlight && <Star className="h-4 w-4 text-accent fill-accent flex-shrink-0" />}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {event.date}
                        {event.time && ` • ${event.time}`}
                      </p>
                      <Badge
                        variant={event.eventType === 'öffentlich' ? 'default' : 'secondary'}
                        className="mt-2 text-xs"
                      >
                        {event.eventType}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {upcomingEvents.length === 0 && (
              <div className="col-span-full">
                <Card>
                  <CardContent className="p-6 text-center text-sm text-muted-foreground">
                    Aktuell sind keine Termine eingetragen.
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Arbeitsdienste */}
      <section className="py-10 lg:py-12 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl lg:text-2xl font-bold text-foreground flex items-center gap-2">
              <Clock className="h-5 w-5 text-accent" />
              Arbeitsdienste
            </h2>
            <Link
              to="/aktivitaeten?tab=workduty"
              className="text-accent hover:text-accent/80 text-sm font-medium flex items-center gap-1"
            >
              Alle anzeigen <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {upcomingWorkDuties.map((duty, idx) => (
                  <div key={duty.id || idx} className="p-4 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-5 w-5 text-accent" />
                    </div>
                    <div className="flex-1 grid sm:grid-cols-3 gap-1">
                      <p className="font-semibold text-foreground">{duty.task}</p>
                      <p className="text-sm text-muted-foreground">
                        {duty.date}
                        {duty.time && ` • ${duty.time}`}
                      </p>
                      {duty.responsible && <p className="text-xs text-muted-foreground">{duty.responsible}</p>}
                    </div>
                  </div>
                ))}
                {upcomingWorkDuties.length === 0 && (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    Aktuell keine Arbeitsdienste geplant.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Aktuelle Meldungen & Erfolge */}
      <section className="py-12 lg:py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-display text-2xl lg:text-3xl font-bold text-foreground">
              Aktuelle Meldungen & Erfolge
            </h2>
            <Link
              to="/aktivitaeten"
              className="text-primary hover:text-primary-dark text-sm font-medium flex items-center gap-1"
            >
              Alle anzeigen <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {latestNews.map((article, idx) => {
              const NewsIcon = getNewsIcon(article);
              const iconColor = getNewsIconColor(article.category);
              return (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <article aria-labelledby={`home-news-${article.id}-title`} className="h-full">
                    <Link to={`/news/${article.id}`} className="block h-full">
                      <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer h-full flex flex-col">
                        <div className="aspect-video relative overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                            loading="lazy"
                            decoding="async"
                            width="800"
                            height="450"
                          />
                        </div>
                        <CardContent className="p-4 flex flex-col flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <NewsIcon className={`h-4 w-4 ${iconColor}`} />
                            <span className="text-xs text-muted-foreground">{article.date}</span>
                          </div>
                          <h3
                            id={`home-news-${article.id}-title`}
                            className="font-display font-semibold text-foreground mb-2 line-clamp-2"
                          >
                            {article.title}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2 flex-1">{article.teaser}</p>
                          <p className="text-sm text-primary font-medium mt-3 flex items-center gap-1">
                            Weiterlesen <ArrowRight className="h-3 w-3" />
                          </p>
                        </CardContent>
                      </Card>
                    </Link>
                  </article>
                </motion.div>
              );
            })}
            {latestNews.length === 0 && (
              <div className="col-span-full">
                <Card>
                  <CardContent className="p-6 text-center text-sm text-muted-foreground">
                    Aktuell keine Beiträge vorhanden.
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          <div className="text-center mt-8">
            <Link to="/aktivitaeten">
              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
              >
                Alle Beiträge anzeigen
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Info Boxes — weitere Vereinsinfos + Kontakt-CTA am Ende */}
      <section className="py-12 lg:py-16 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {infoBoxes.map((box, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link to={box.link}>
                  <Card className="overflow-hidden hover:shadow-xl transition-all hover:-translate-y-1 group">
                    <div className="aspect-[4/3] relative overflow-hidden">
                      <img
                        src={box.image}
                        alt={box.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        decoding="async"
                        width="800"
                        height="600"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <h3 className="font-display text-xl font-bold mb-2">{box.title}</h3>
                        <p className="text-sm text-white/80">{box.description}</p>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
